import React from "react"
import { Formik, Form, Field } from "formik"
import { useTranslation } from "react-i18next"
import { FormField, Input } from "@bw/forms"
import { Spacer, Button } from "@bw/bits"
import * as Icons from "@bw/icons"
import Fuse from "fuse.js"
import { toast } from "react-toastify"
import calculateDistance from "../../../utils/calculateDistance"

const initialValues = {
  textSearch: "",
  position: null,
}

let timer

const Search = ({ pois, setFilteredPois }) => {
  const { t } = useTranslation()
  const [filter, setFilter] = React.useState(initialValues)

  /**
   * create index
   */
  const fuse = React.useMemo(() => {
    return new Fuse(pois, {
      keys: [
        {
          name: "title",
          weight: 1,
        },
        {
          name: "city",
          weight: 3,
        },
      ],
      minMatchCharLength: 3,
      ignoreLocation: true,
      threshold: 0.2,
    })
  }, [pois])

  React.useEffect(() => {
    let filtered = pois

    /**
     * Filter by searchtext
     */
    if (fuse && filter.textSearch.length > 2) {
      filtered = fuse.search(filter.textSearch).map(result => result.item)
    }

    /**
     * Filter by position
     */
    if (filter.position !== null) {
      for (let i = 0; i < filtered.length; i++) {
        filtered[i].distance = calculateDistance(
          filter.position.latitude,
          filter.position.longitude,
          filtered[i].latitude,
          filtered[i].longitude
        )
      }

      filtered = filtered.filter(
        poi => poi.distance <= 20 // radius in km
      )

      filtered.sort((a, b) => a.distance - b.distance)
    }

    if (filtered.length === 0) {
      toast.error(t("search.noResult"))
    }

    setFilteredPois(filtered)
  }, [pois, filter, t, fuse, setFilteredPois])

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={values => {
        setFilter(values)
      }}
    >
      {({ submitForm, handleReset }) => (
        <Form
          onChange={e => {
            if (e.target.name === "textSearch") {
              clearTimeout(timer)
              timer = setTimeout(submitForm, 500)
            } else {
              submitForm()
            }
          }}
        >
          <Spacer direction="row" justify="space-between">
            <FormField title={t("listOfPois.textSearch")} name="textSearch">
              <Field
                name="textSearch"
                component={Input}
                icon={<Icons.Search />}
                autoComplete="off"
              />
            </FormField>
            {typeof navigator !== "undefined" && navigator.geolocation && (
              <FormField title={t("listOfPois.poximity")} name="proximity">
                <Button
                  small
                  label={
                    filter.position === null
                      ? t("listOfPois.buttonProximity")
                      : t("listOfPois.buttonProximityCancel")
                  }
                  onClick={() => {
                    if (filter.position !== null) {
                      setFilter(values => ({ ...values, position: null }))
                      return
                    }

                    handleReset()
                    navigator.geolocation.getCurrentPosition(
                      position => {
                        setFilter({
                          ...initialValues,
                          position: {
                            longitude: position.coords.longitude,
                            latitude: position.coords.latitude,
                          },
                        })
                      },
                      error => {
                        // check if the user denied geolocation, or if there was any other problem
                        if (error.code === error.PERMISSION_DENIED) {
                          alert("Geolocation has been disabled on this page")
                        } else {
                          alert(
                            "Unable to find your position, try again later."
                          )
                        }
                      },
                      {
                        enableHighAccuracy: true,
                        timeout: 10000,
                      }
                    )
                  }}
                />
              </FormField>
            )}
          </Spacer>
        </Form>
      )}
    </Formik>
  )
}

export default Search
